import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  _userRole,
  calculateTotalTime,
  checkEntryExit,
  formatTime,
  startRunningTime,
} from "../../../../../utils/common";
import useUserRole from "../../../../../hooks/useUserRole";
import {
  _endStaffAttendance,
  _fetchActiveAttendance,
  _startStaffAttendance,
} from "../../../../../store/actions/staff";
import DefaultButton from "../../../../components/buttons/defaultButton";
import CommonModal from "../../../../components/modals/commonModel";
import { MdOutlineCalendarMonth } from "react-icons/md";
import DefaultAvatar from "../../../../components/avatar/defaultAvatar";
import toast from "react-hot-toast";
import useCurrentLocation from "../../../../../hooks/useCurrentLocation";
import locationFailed from "../../../../assets/images/location.png";
import moment from "moment";
import DefaultTextarea from "../../../../components/inputs/defaultTextarea";
import { LuAlarmClockMinus, LuAlarmClockPlus } from "react-icons/lu";

const checkAndFilterEndTime = (data) => {
  // Find the first item with no end_time
  const filteredItem = data.find((item) => !item.end_time);

  return {
    hasEndTime: !!filteredItem, // true if an item without end_time is found
    filteredData: filteredItem || null, // Return the found item or null if not found
  };
};

// Helper function to get UUID from activeData regardless of structure
const getAttendanceUuid = (activeData) => {
  if (!activeData) return null;
  // Handle both structures: { uuid: ... } and { attendance: { uuid: ... } }
  return activeData.uuid || activeData?.attendance?.uuid || null;
};

const HandleAttendance = React.memo(() => {
  const activeAttendance = useSelector((state) => state.user.activeAttendance);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeData, setActiveData] = useState(null);
  const [runningTime, setRunningTime] = useState("");
  const intervalRef = useRef(null);
  const role = useUserRole();

  const TotalTime = useMemo(
    () => calculateTotalTime(activeAttendance),
    [activeAttendance]
  );

  const openModal = () => setIsModalOpen(true);
  const onClose = () => setIsModalOpen(false);

  const callback = useCallback((error, response) => {
    setLoading(false);
    if (error) return;
  }, []);

  const handleFetchData = useCallback(() => {
    if (role === _userRole.WORKSPACE_ADMIN) return;
    setLoading(true);
    clearInterval(intervalRef.current);
    dispatch(_fetchActiveAttendance(callback));
  }, [role]);

  useEffect(() => {
    handleFetchData();
  }, []); // Only run on mount

  useEffect(() => {
    if (activeAttendance?.length) {
      const result = checkAndFilterEndTime(activeAttendance);
      setActiveData(result.hasEndTime ? result.filteredData : null);
    } else {
      setActiveData(null);
    }
  }, [activeAttendance]);

  const updateTime = useCallback((newTime) => {
    setRunningTime((prev) => (prev !== newTime ? newTime : prev));
  }, []);

  useEffect(() => {
    if (!activeData) {
      setRunningTime("");
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Use TotalTime directly (same as frontend)
    // TotalTime is already memoized and calculated from activeAttendance
    intervalRef.current = startRunningTime(TotalTime, updateTime);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeData, updateTime]);

  const memoizedActiveData = useMemo(() => activeData, [activeData]);
  const memoizedOnClose = useCallback(() => setIsModalOpen(false), []);

  return (
    <div className="">
      {loading ? (
        <div className="bg-gray-100 p-1 pl-2 rounded-md">
          <div className="flex flex-row items-center gap-2">
            <div className="flex flex-col">
              <p className="animate-pulse bg-bghoverlight mb-[2px] rounded-sm w-[100px] h-[15px]"></p>
              <p className="animate-pulse bg-bghoverlight rounded-sm w-[100px] h-[15px]"></p>
            </div>
            <div className="w-[20px] rounded-md h-[32px] animate-pulse bg-bghoverlight"></div>
          </div>
        </div>
      ) : activeData ? (
        // ✅ Attendance RUNNING — green theme (white header ke liye)
        <div
          className="bg-green-50 border border-green-200 p-1 pl-4 rounded-md cursor-pointer hover:bg-green-100 transition-colors"
          onClick={openModal}
        >
          <div className="flex flex-row gap-3 items-center">
            <div className="flex flex-col">
              <p className="text-green-600 text-[10px] font-medium">Today Time</p>
              <p className="text-green-800 leading-3 font-bold text-sm">{runningTime}</p>
            </div>
            <span className="text-green-600 text-2xl">
              <LuAlarmClockPlus />
            </span>
          </div>
        </div>
      ) : (
        // ✅ Attendance NOT running — red theme
        <div
          className="bg-red-50 border border-red-200 p-1 pl-4 rounded-md cursor-pointer hover:bg-red-100 transition-colors"
          onClick={openModal}
        >
          <div className="flex flex-row items-center gap-3">
            <div className="flex flex-col">
              <p className="text-red-500 text-[10px] font-medium">Today Time</p>
              <p className="text-red-700 leading-3 font-bold text-sm">{TotalTime}</p>
            </div>
            <span className="text-red-500 text-2xl">
              <LuAlarmClockMinus />
            </span>
          </div>
        </div>
      )}

      {isModalOpen && (
        <CommonModal
          modalSize="sm"
          headerTitle=""
          onClose={onClose}
          show={isModalOpen}
        >
          <AttendanceCard
            handleFetchData={handleFetchData}
            activeData={memoizedActiveData}
            onClose={memoizedOnClose}
            setActiveData={setActiveData}
          />
        </CommonModal>
      )}
    </div>
  );
});

export default HandleAttendance;

const AttendanceCard = React.memo(
  ({
    handleFetchData = () => null,
    activeData = null,
    setActiveData,
    onClose = () => null,
  }) => {
    const userInfo = useSelector((state) => state?.user?.userInfo);
    const user = userInfo?.user || { name: "User" };
    // Since we removed workspace slice, provide a fallback
    const workspace = {
      default_workspace: {
        name: "School",
      },
    };
    const { latitude, longitude, error } = useCurrentLocation();
    const [loading, setLoading] = useState(false);
    const default_workspace = workspace?.default_workspace || null;
    const [checkResult, setCheckResult] = useState(null);
    const [reason, setReason] = useState("");
    const dispatch = useDispatch();

    const callback_start = (hasError, response) => {
      setLoading(false);
      if (hasError) {
        toast.error(
          response?.response?.data?.message ||
            response?.message ||
            "Failed to start attendance. Please try again."
        );
        return;
      }

      if (response) {
        // 👇 update UI instantly
        setActiveData(response);
      }

      // Fetch updated attendance list to refresh total time
      handleFetchData();
      toast.success("Attendance started successfully.");
      onClose();
    };

    const callback_end = (hasError, response) => {
      setLoading(false);
      if (hasError) {
        toast.error(
          response?.response?.data?.message ||
            response?.message ||
            "Failed to end attendance. Please try again."
        );
        return;
      }

      if (response) {
        // 👇 clear instantly
        setActiveData(null);
      }

      // Fetch updated attendance list to refresh total time
      handleFetchData();
      toast.success("Attendance ended successfully.");
      onClose();
    };

    const handleStartAttendance = () => {
      if (checkResult?.lateEntry && !reason) {
        return toast.error("Please provide a reason for late entry.");
      }
      setLoading(true);
      const currentDate = new Date();
      const currentStartTime = currentDate.toTimeString().split(" ")[0]; // Extracts HH:MM:SS format
      const payload = {
        user_id: user.id,
        date: currentDate.toISOString().split("T")[0], // Format as YYYY-MM-DD
        start_time: currentStartTime, // Current time in HH:MM:SS format
        end_time: "",
        start_location: "",
        start_latitude: latitude,
        start_longitude: longitude,
        late_entry: reason || "",
        skip: true,
      };
      dispatch(_startStaffAttendance(payload, callback_start));
    };
 
    const handleEndAttendance = () => {
      if (checkResult?.earlyExit && !reason) {
        return toast.error("Please provide a reason for early exit.");
      }
      
      const attendanceUuid = getAttendanceUuid(activeData);
      if (!attendanceUuid) {
        toast.error("Attendance ID not found. Please try again.");
        return;
      }

      setLoading(true);
      const currentDate = new Date();
      const currentStartTime = currentDate.toTimeString().split(" ")[0]; // Extracts HH:MM:SS format
      
      // Extract the base data depending on structure
      const baseData = activeData?.attendance || activeData;
      
      const payload = {
        ...baseData,
        end_time: currentStartTime,
        end_location: "",
        end_latitude: latitude,
        end_longitude: longitude,
        early_exit: reason || "",
      };
      
      dispatch(
        _endStaffAttendance(attendanceUuid, payload, callback_end)
      );
    };

    // useEffect to handle the side effect
    useEffect(() => {
      // Check if the default_workspace exists and is not null
      if (default_workspace) {
        const check = checkEntryExit(default_workspace);
        setCheckResult(check); // Set the check result in state
      }
    }, [default_workspace]); // This will re-run when default_workspace changes

    
    return (
      <div className="flex items-center gap-4 flex-col">
        <div className="items-center justify-center flex-col flex">
          <div className="rounded-full w-20 h-20 flex-center border">
            <DefaultAvatar image={user?.profile_image || null} />
          </div>
          <p className="text-theme font-bold">{user?.name || "-"}</p>
          <span className="flex flex-row gap-1 items-center text-theme">
            <MdOutlineCalendarMonth />
            <p className="text-textLight">{moment().format("DD MMM YYYY")}</p>
          </span>
        </div>

        {error ? (
          <div className="flex flex-col gap-1">
            <p className="text-sm text-red-800 text-center">
              Location access is required. Please enable it to start or stop
              attendance.
            </p>
            <div className="w-full h-[250px] rounded-md overflow-hidden">
              <img className="" src={locationFailed} alt={"Location Failed!"} />
            </div>
          </div>
        ) : (
          <Fragment>

            {(activeData && checkResult?.earlyExit) ||
              (!activeData && checkResult?.lateEntry) ? (
              <DefaultTextarea
                label={activeData ? "Early Exit Reason" : "Late Entry Reason"}
                name="reason"
                value={reason}
                wordCount={false}
                onChange={(e) => {
                  const input = e.target.value;
                  if (input.length <= 300) {
                    setReason(input);
                  }
                }}
                required
              />
            ) : null}

            {activeData ? (
              <div className="items-center justify-center flex-col flex">
                <DefaultButton
                  loading={loading}
                  onClick={handleEndAttendance}
                  variant="danger"
                  name={"End Attendance"}
                />
              </div>
            ) : (
              <div className="items-center justify-center flex-col flex">
                <DefaultButton
                  loading={loading}
                  onClick={handleStartAttendance}
                  variant="success"
                  name={"Start Attendance"}
                />
              </div>
            )}
          </Fragment>
        )}
      </div>
    );
  }
);
